import { useRef, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { uploadPhoto } from "../lib/upload";
import { Watermark } from "./Watermark";
import { can } from "@family/shared";
import type {
  CommentsListResponse,
  EventAlbumsResponse,
  EventMediaItem,
  EventMediaResponse,
  Role,
  WatermarkConfig,
} from "@family/shared";

interface EventGalleryProps {
  familySlug: string;
  eventId: string;
  role: Role;
  currentUserId: string | null;
  watermarkConfig: WatermarkConfig;
  watermarkVars: {
    user: string;
    family: string;
    phone: string | null;
    email: string | null;
  };
}

// ── Media viewer modal ────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function AuthorAvatar({ name }: { name: string | null }) {
  const initials = (name ?? "?").slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
      background: "linear-gradient(135deg, var(--c-accent), var(--c-accent-hover))",
      color: "var(--c-accent-contrast)", fontWeight: 700, fontSize: "0.7rem",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {initials}
    </div>
  );
}

interface MediaViewerProps {
  item: EventMediaItem;
  familySlug: string;
  eventId: string;
  watermarkConfig: WatermarkConfig;
  watermarkVars: EventGalleryProps["watermarkVars"];
  canManage: boolean;
  canDownload: boolean;
  currentUserId: string | null;
  onClose: () => void;
  onDeleted: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}

function MediaViewer({
  item,
  familySlug,
  eventId,
  watermarkConfig,
  watermarkVars,
  canManage,
  canDownload,
  currentUserId,
  onClose,
  onDeleted,
  onPrev,
  onNext,
}: MediaViewerProps) {
  const queryClient = useQueryClient();
  const [commentBody, setCommentBody] = useState("");

  const deleteMedia = useMutation({
    mutationFn: () =>
      apiFetch(
        `/families/${familySlug}/events/${eventId}/media/${item.id}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["event-media", familySlug, eventId] });
      void queryClient.invalidateQueries({ queryKey: ["event-albums", familySlug, eventId] });
      onDeleted();
    },
  });

  // Reuse the event-media-comments query key
  const commentsQuery = useQuery<CommentsListResponse>({
    queryKey: ["event-media-comments", familySlug, eventId, item.mediaId],
    queryFn: () =>
      apiFetch<CommentsListResponse>(
        `/families/${familySlug}/media/${item.mediaId}/comments`,
      ),
  });

  const addComment = useMutation({
    mutationFn: (body: string) =>
      apiFetch(`/families/${familySlug}/media/${item.mediaId}/comments`, {
        method: "POST",
        body: JSON.stringify({ body }),
      }),
    onSuccess: () => {
      setCommentBody("");
      void queryClient.invalidateQueries({
        queryKey: ["event-media-comments", familySlug, eventId, item.mediaId],
      });
    },
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(0,0,0,0.92)",
        display: "flex",
        flexDirection: "column",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        color: "#fff",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: "0.95rem", fontWeight: item.caption ? 700 : 400 }}>
            {item.caption || item.uploadedBy.displayName || "Unknown"}
          </span>
          {item.caption && (
            <span style={{ fontSize: "0.8rem", opacity: 0.6 }}>
              by {item.uploadedBy.displayName ?? "Unknown"}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {(canManage || item.uploadedBy.id === currentUserId) && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm("Remove this media from the event? The file will be deleted.")) {
                  deleteMedia.mutate();
                }
              }}
              disabled={deleteMedia.isPending}
              style={{
                background: "rgba(220,38,38,0.7)",
                border: "none",
                color: "#fff",
                borderRadius: "var(--r-pill, 999px)",
                padding: "6px 14px",
                minHeight: 34,
                fontSize: "0.85rem",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {deleteMedia.isPending ? "Deleting…" : "Delete"}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "none",
              color: "#fff",
              borderRadius: "50%",
              width: 36, height: 36, minHeight: 36,
              fontSize: "1.2rem",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="gallery-viewer__body" style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>
        {/* Media */}
        <div className="gallery-viewer__media" style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          minWidth: 0,
          background: "#000",
        }}>
          {onPrev && (
            <button
              type="button"
              onClick={onPrev}
              style={{
                position: "absolute", left: 12, zIndex: 1,
                background: "rgba(255,255,255,0.15)", border: "none",
                color: "#fff", borderRadius: "50%",
                width: 44, height: 44, minHeight: 44,
                fontSize: "1.4rem", cursor: "pointer",
              }}
            >‹</button>
          )}

          <div style={{ position: "relative", maxWidth: "100%", maxHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {item.kind === "video" ? (
              (() => {
                const ext = item.viewUrl.split("?")[0]?.split(".").pop()?.toLowerCase() ?? "";
                const browserPlayable = ["mp4", "webm", "ogg", "mov"].includes(ext);
                if (!browserPlayable) {
                  return (
                    <div style={{
                      color: "#fff", textAlign: "center", padding: 32,
                      display: "flex", flexDirection: "column", gap: 16, alignItems: "center",
                    }}>
                      <span style={{ fontSize: "3rem" }}>🎬</span>
                      <p style={{ margin: 0, opacity: 0.8 }}>
                        This video format (.{ext}) cannot be played directly in the browser.
                      </p>
                      {canDownload && (
                        <a
                          href={item.viewUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            padding: "10px 24px", background: "#fff", color: "#111",
                            borderRadius: 999, fontWeight: 700, textDecoration: "none",
                            fontSize: "0.95rem",
                          }}
                        >
                          Open / Download
                        </a>
                      )}
                      <p style={{ margin: 0, fontSize: "0.8rem", opacity: 0.5 }}>
                        {canDownload ? "Tip: convert to MP4 for in-browser playback" : "This video is being processed or requires admin access."}
                      </p>
                    </div>
                  );
                }
                return (
                  <video
                    src={item.viewUrl}
                    controls
                    controlsList={canDownload ? undefined : "nodownload"}
                    style={{
                      width: "100%",
                      maxHeight: "calc(100vh - 120px)",
                      display: "block",
                      background: "#000",
                    }}
                    onContextMenu={(e) => { if (!canDownload) e.preventDefault(); }}
                  />
                );
              })()
            ) : (
              <img
                src={item.viewUrl}
                alt=""
                style={{
                  maxWidth: "100%",
                  maxHeight: "calc(100vh - 120px)",
                  display: "block",
                  userSelect: "none",
                  pointerEvents: "none",
                }}
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
              />
            )}
            {item.kind !== "video" && (
              <Watermark config={watermarkConfig} variables={watermarkVars} />
            )}
          </div>

          {onNext && (
            <button
              type="button"
              onClick={onNext}
              style={{
                position: "absolute", right: 12, zIndex: 1,
                background: "rgba(255,255,255,0.15)", border: "none",
                color: "#fff", borderRadius: "50%",
                width: 44, height: 44, minHeight: 44,
                fontSize: "1.4rem", cursor: "pointer",
              }}
            >›</button>
          )}
        </div>

        {/* Comments sidebar */}
        <div className="gallery-viewer__sidebar" style={{
          flexShrink: 0,
          background: "var(--c-bg)",
          display: "flex",
          flexDirection: "column",
        }}>
          <div style={{
            padding: "12px 16px",
            borderBottom: "1px solid var(--c-border)",
            fontWeight: 700,
            fontSize: "0.9rem",
          }}>
            Comments
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
            {commentsQuery.isLoading && (
              <p className="text-muted" style={{ fontSize: "0.85rem" }}>Loading…</p>
            )}
            {commentsQuery.data?.comments.length === 0 && (
              <p className="text-muted" style={{ fontSize: "0.85rem" }}>
                No comments yet.
              </p>
            )}
            {commentsQuery.data?.comments.map((c) => (
              <div key={c.id} style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <AuthorAvatar name={c.author.displayName} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: "0.85rem" }}>
                      {c.author.displayName ?? "Unknown"}
                    </span>
                    <span className="text-subtle" style={{ fontSize: "0.75rem" }}>
                      {timeAgo(c.createdAt)}
                    </span>
                  </div>
                  <p style={{ margin: "2px 0 0", fontSize: "0.9rem", lineHeight: 1.5 }}>
                    {c.body}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (commentBody.trim()) addComment.mutate(commentBody.trim());
            }}
            style={{
              padding: "12px 16px",
              borderTop: "1px solid var(--c-border)",
              display: "flex",
              gap: 8,
            }}
          >
            <input
              type="text"
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              placeholder="Write a comment…"
              disabled={addComment.isPending}
              style={{
                flex: 1, padding: "8px 12px", fontSize: "0.9rem",
                border: "1px solid var(--c-border-strong)", borderRadius: 8,
                background: "var(--c-bg)", color: "var(--c-text)",
                minHeight: 0,
              }}
            />
            <button
              type="submit"
              disabled={addComment.isPending || !commentBody.trim()}
              style={{ padding: "8px 14px", fontSize: "0.85rem", borderRadius: 8, minHeight: 0 }}
            >
              {addComment.isPending ? "…" : "Post"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// Video formats ffmpeg can transcode to MP4
const ACCEPTED_VIDEO_TYPES = new Set([
  "video/mp4",
  "video/webm",
  "video/ogg",
  "video/quicktime",   // .mov
  "video/mpeg",        // .mpg, .mpeg
  "video/x-msvideo",  // .avi
  "video/x-ms-wmv",   // .wmv
  "video/x-matroska",  // .mkv
  "video/3gpp",        // .3gp
  "video/x-flv",       // .flv
  "video/mp2t",        // .ts
]);

const ACCEPTED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
]);

function validateFile(file: File): string | null {
  const mime = file.type;
  if (mime.startsWith("video/")) {
    if (!ACCEPTED_VIDEO_TYPES.has(mime)) {
      const ext = file.name.split(".").pop()?.toUpperCase() ?? "this format";
      return `${ext} is not a supported video format. Supported: MP4, MOV, AVI, MPG, MKV, WMV, WebM, FLV, 3GP.`;
    }
  } else if (mime.startsWith("image/")) {
    if (!ACCEPTED_IMAGE_TYPES.has(mime)) {
      return `This image format is not supported. Please use JPEG, PNG, or WebP.`;
    }
  } else {
    return `Unsupported file type: ${mime || file.name}`;
  }
  return null;
}

export function EventGallery({
  familySlug,
  eventId,
  role,
  currentUserId,
  watermarkConfig,
  watermarkVars,
}: EventGalleryProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [activeAlbum, setActiveAlbum] = useState<string | null>(null);
  const [newAlbumTitle, setNewAlbumTitle] = useState("");
  const [showAddAlbum, setShowAddAlbum] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  const canManage = can(role, "events:manage");
  const canUpload = can(role, "post:create");

  const albumsQuery = useQuery<EventAlbumsResponse>({
    queryKey: ["event-albums", familySlug, eventId],
    queryFn: () =>
      apiFetch<EventAlbumsResponse>(
        `/families/${familySlug}/events/${eventId}/albums`,
      ),
  });

  const mediaQuery = useQuery<EventMediaResponse>({
    queryKey: ["event-media", familySlug, eventId, activeAlbum],
    queryFn: () => {
      const url = activeAlbum
        ? `/families/${familySlug}/events/${eventId}/media?albumId=${activeAlbum}`
        : `/families/${familySlug}/events/${eventId}/media`;
      return apiFetch<EventMediaResponse>(url);
    },
    // Poll every 5s if any items are still transcoding
    refetchInterval: (data) => {
      const items = data?.state?.data?.media ?? [];
      return items.some((m) => m.status === "transcoding") ? 5000 : false;
    },
  });

  const createAlbum = useMutation({
    mutationFn: (title: string) =>
      apiFetch(`/families/${familySlug}/events/${eventId}/albums`, {
        method: "POST",
        body: JSON.stringify({ title }),
      }),
    onSuccess: () => {
      setNewAlbumTitle("");
      setShowAddAlbum(false);
      void queryClient.invalidateQueries({ queryKey: ["event-albums", familySlug, eventId] });
    },
  });

  const deleteAlbum = useMutation({
    mutationFn: (albumId: string) =>
      apiFetch(`/families/${familySlug}/events/${eventId}/albums/${albumId}`, { method: "DELETE" }),
    onSuccess: () => {
      setActiveAlbum(null);
      void queryClient.invalidateQueries({ queryKey: ["event-albums", familySlug, eventId] });
      void queryClient.invalidateQueries({ queryKey: ["event-media", familySlug, eventId] });
    },
  });

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length === 0) return;

    // Validate all files before starting any upload
    for (const file of files) {
      const err = validateFile(file);
      if (err) {
        setUploadError(err);
        return;
      }
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    try {
      const mediaIds: string[] = [];
      const captions: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        const mediaId = await uploadPhoto(familySlug, file, {
          onProgress: (pct) => {
            setUploadProgress(Math.round(((i + pct / 100) / files.length) * 100));
          },
        });
        mediaIds.push(mediaId);
        // Use filename without extension as caption
        const name = file.name.replace(/\.[^.]+$/, "").replace(/[_-]/g, " ");
        captions.push(name);
      }
      await apiFetch(`/families/${familySlug}/events/${eventId}/media`, {
        method: "POST",
        body: JSON.stringify({ mediaIds, captions, albumId: activeAlbum ?? undefined }),
      });
      void queryClient.invalidateQueries({ queryKey: ["event-media", familySlug, eventId] });
      void queryClient.invalidateQueries({ queryKey: ["event-albums", familySlug, eventId] });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  }

  const albums = albumsQuery.data?.albums ?? [];
  const mediaItems = mediaQuery.data?.media ?? [];
  const totalMedia = albums.reduce((sum, a) => sum + (a.mediaCount ?? 0), 0);

  const pillStyle = (active: boolean): React.CSSProperties => ({
    padding: "6px 14px", minHeight: 36,
    borderRadius: "var(--r-pill, 999px)", border: "1px solid",
    borderColor: active ? "var(--c-blue, #0058a3)" : "var(--c-border)",
    background: active ? "var(--c-blue, #0058a3)" : "transparent",
    color: active ? "#fff" : "var(--c-text)",
    fontWeight: active ? 700 : 400, fontSize: "0.85rem", cursor: "pointer",
  });

  return (
    <section className="stack">
      {/* Viewer modal */}
      {viewerIndex !== null && mediaItems[viewerIndex] && (
        <MediaViewer
          item={mediaItems[viewerIndex]!}
          familySlug={familySlug}
          eventId={eventId}
          watermarkConfig={watermarkConfig}
          watermarkVars={watermarkVars}
          canManage={canManage}
          canDownload={canManage}
          currentUserId={currentUserId}
          onClose={() => setViewerIndex(null)}
          onDeleted={() => {
            // If last item, close; otherwise stay at same index (next item shifts in)
            if (mediaItems.length <= 1) {
              setViewerIndex(null);
            } else if (viewerIndex >= mediaItems.length - 1) {
              setViewerIndex(viewerIndex - 1);
            }
          }}
          onPrev={viewerIndex > 0 ? () => setViewerIndex(viewerIndex - 1) : undefined}
          onNext={viewerIndex < mediaItems.length - 1 ? () => setViewerIndex(viewerIndex + 1) : undefined}
        />
      )}

      {/* Album pills */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button type="button" onClick={() => setActiveAlbum(null)} style={pillStyle(!activeAlbum)}>
          All ({totalMedia || mediaItems.length})
        </button>
        {albums.map((album) => (
          <button key={album.id} type="button" onClick={() => setActiveAlbum(album.id)}
            style={pillStyle(activeAlbum === album.id)}>
            {album.title} ({album.mediaCount ?? 0})
          </button>
        ))}
        {canManage && (
          <button type="button" onClick={() => setShowAddAlbum(!showAddAlbum)}
            style={{
              padding: "6px 12px", minHeight: 36,
              borderRadius: "var(--r-pill, 999px)",
              border: "1px dashed var(--c-border-strong)",
              background: "transparent", color: "var(--c-text-subtle)",
              fontSize: "0.85rem", cursor: "pointer",
            }}>
            + Album
          </button>
        )}
      </div>

      {/* Add album form */}
      {showAddAlbum && canManage && (
        <form onSubmit={(e) => { e.preventDefault(); if (newAlbumTitle.trim()) createAlbum.mutate(newAlbumTitle.trim()); }}
          style={{ display: "flex", gap: 8 }}>
          <input type="text" value={newAlbumTitle} onChange={(e) => setNewAlbumTitle(e.target.value)}
            placeholder="Album name…"
            style={{ flex: 1, padding: "8px 12px", fontSize: "0.9rem", border: "1px solid var(--c-border-strong)", borderRadius: 8, background: "var(--c-bg)", color: "var(--c-text)" }} />
          <button type="submit" disabled={createAlbum.isPending || !newAlbumTitle.trim()}
            style={{ padding: "8px 16px", fontSize: "0.85rem", borderRadius: 8 }}>
            {createAlbum.isPending ? "…" : "Add"}
          </button>
        </form>
      )}

      {/* Delete album */}
      {activeAlbum && canManage && (
        <button type="button" className="btn-tertiary"
          onClick={() => {
            const album = albums.find((a) => a.id === activeAlbum);
            if (album && window.confirm(`Delete album "${album.title}"?`)) deleteAlbum.mutate(activeAlbum);
          }}
          disabled={deleteAlbum.isPending}
          style={{ color: "var(--c-danger)", fontSize: "0.85rem", alignSelf: "flex-start" }}>
          Delete this album
        </button>
      )}

      {/* Upload */}
      {canUpload && (
        <div>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,video/mp4,video/webm,video/ogg,video/quicktime,video/mpeg,video/x-msvideo,video/x-ms-wmv,video/x-matroska,video/3gpp,video/x-flv,video/mp2t,.mpg,.mpeg,.avi,.wmv,.mkv,.flv,.3gp,.ts,.mov" multiple
            onChange={handleUpload} style={{ display: "none" }} />
          <button type="button" className="btn-secondary"
            onClick={() => fileInputRef.current?.click()} disabled={uploading}
            style={{ fontSize: "0.9rem" }}>
            {uploading
              ? `Uploading… ${uploadProgress}%`
              : `Upload to ${activeAlbum ? albums.find((a) => a.id === activeAlbum)?.title ?? "album" : "event"}`}
          </button>
          {uploadError && (
            <p className="text-error" style={{ fontSize: "0.85rem", marginTop: 4 }}>{uploadError}</p>
          )}
        </div>
      )}

      {mediaQuery.isLoading && <p className="text-muted">Loading media…</p>}

      {mediaItems.length === 0 && !mediaQuery.isLoading && (
        <p className="text-muted">
          No photos or videos yet.{canUpload && " Upload some to get started."}
        </p>
      )}

      {/* Media grid */}
      {mediaItems.length > 0 && (() => {
        const photos = mediaItems.filter((m) => m.kind !== "video");
        const videos = mediaItems.filter((m) => m.kind === "video");
        const hasPhotos = photos.length > 0;
        const hasVideos = videos.length > 0;
        const showHeaders = hasPhotos && hasVideos;

        return (
          <div className="stack">
            {hasPhotos && (
              <>
                {showHeaders && <h4 style={{ margin: 0, fontSize: "0.95rem" }}>📷 Photos ({photos.length})</h4>}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: "var(--s-2)",
                }}>
                  {photos.map((item) => {
                    const idx = mediaItems.indexOf(item);
                    return (
                      <div
                        key={item.id}
                        onClick={() => item.status !== "transcoding" && setViewerIndex(idx)}
                        style={{
                          position: "relative", overflow: "hidden", borderRadius: 8,
                          background: "var(--c-bg-alt)",
                          cursor: item.status === "transcoding" ? "default" : "pointer",
                        }}
                      >
                        {item.status === "transcoding" ? (
                          <div style={{
                            aspectRatio: "16/9", background: "#1a1a2e",
                            display: "flex", flexDirection: "column",
                            alignItems: "center", justifyContent: "center", gap: 8,
                            color: "#fff",
                          }}>
                            <span style={{ fontSize: "1.5rem" }}>⏳</span>
                            <span style={{ fontSize: "0.75rem", opacity: 0.7 }}>Converting…</span>
                          </div>
                        ) : (
                          <img
                            src={item.viewUrl}
                            alt=""
                            loading="lazy"
                            style={{
                              width: "100%", height: "auto", display: "block",
                              userSelect: "none", pointerEvents: "none",
                            }}
                            draggable={false}
                            onContextMenu={(e) => e.preventDefault()}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {hasVideos && (
              <>
                {showHeaders && <h4 style={{ margin: 0, fontSize: "0.95rem" }}>🎬 Videos ({videos.length})</h4>}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: "var(--s-2)",
                }}>
                  {videos.map((item) => {
                    const idx = mediaItems.indexOf(item);
                    return (
                      <div
                        key={item.id}
                        onClick={() => item.status !== "transcoding" && setViewerIndex(idx)}
                        style={{
                          position: "relative", overflow: "hidden", borderRadius: 8,
                          background: "var(--c-bg-alt)",
                          cursor: item.status === "transcoding" ? "default" : "pointer",
                        }}
                      >
                        {item.status === "transcoding" ? (
                          <div style={{
                            aspectRatio: "16/9", background: "#1a1a2e",
                            display: "flex", flexDirection: "column",
                            alignItems: "center", justifyContent: "center", gap: 8,
                            color: "#fff",
                          }}>
                            <span style={{ fontSize: "1.5rem" }}>⏳</span>
                            <span style={{ fontSize: "0.75rem", opacity: 0.7 }}>Converting…</span>
                          </div>
                        ) : (
                          <div style={{
                            aspectRatio: "16/9",
                            background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            position: "relative",
                            overflow: "hidden",
                          }}>
                            {item.thumbnailUrl ? (
                              <img
                                src={item.thumbnailUrl}
                                alt=""
                                loading="lazy"
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : null}
                            <span style={{ fontSize: "2rem", zIndex: 1, textShadow: "0 2px 8px rgba(0,0,0,0.6)" }}>▶</span>
                            {item.caption && !item.thumbnailUrl && (
                              <span style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.7)", maxWidth: "80%", textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {item.caption}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        );
      })()}
    </section>
  );
}
